import { useState } from 'react';

function TrainingPrompts({ prompts, onRecordPrompt }) {
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [completedPrompts, setCompletedPrompts] = useState(new Set());

  const currentPrompt = prompts?.[currentPromptIndex];
  const totalDuration = prompts?.reduce((sum, p) => sum + p.duration_estimate, 0) || 0;
  const completedDuration = prompts?.filter((_, i) => completedPrompts.has(i))
    .reduce((sum, p) => sum + p.duration_estimate, 0) || 0;

  const handlePromptComplete = (index) => {
    setCompletedPrompts(prev => new Set([...prev, index]));
    if (index < prompts.length - 1) {
      setCurrentPromptIndex(index + 1);
    }
  };

  if (!prompts || prompts.length === 0) {
    return null;
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">📝 Training Scripts</h3>
        <span className="text-sm text-gray-400">
          {completedPrompts.size} / {prompts.length} recorded
        </span>
      </div>

      <p className="text-gray-400 text-sm mb-4">
        Read these sentences aloud for best voice cloning results. 
        They cover various sounds, emotions, and speaking styles.
      </p>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Recording progress</span>
          <span>~{completedDuration}s / {totalDuration}s</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div
            className="bg-purple-500 h-2 rounded-full transition-all"
            style={{ width: `${(completedPrompts.size / prompts.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Current prompt */}
      <div className="bg-gray-800 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-purple-400 uppercase tracking-wide">
            {currentPrompt.category}
          </span>
          <span className="text-xs text-gray-500">
            ~{currentPrompt.duration_estimate}s
          </span>
        </div>
        <p className="text-lg leading-relaxed">
          "{currentPrompt.text}"
        </p>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setCurrentPromptIndex(Math.max(0, currentPromptIndex - 1))}
          disabled={currentPromptIndex === 0}
          className="btn btn-secondary text-sm"
        >
          ← Previous
        </button>
        
        <div className="flex space-x-2">
          {prompts.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPromptIndex(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentPromptIndex
                  ? 'bg-purple-500'
                  : completedPrompts.has(index)
                  ? 'bg-green-500'
                  : 'bg-gray-600'
              }`}
            />
          ))}
        </div>
        
        <button
          onClick={() => setCurrentPromptIndex(Math.min(prompts.length - 1, currentPromptIndex + 1))}
          disabled={currentPromptIndex === prompts.length - 1}
          className="btn btn-secondary text-sm"
        >
          Next →
        </button>
      </div>

      {/* Mark as recorded */}
      <div className="mt-4 pt-4 border-t border-gray-700">
        <button
          onClick={() => handlePromptComplete(currentPromptIndex)}
          disabled={completedPrompts.has(currentPromptIndex)}
          className={`w-full py-2 rounded-lg text-sm transition-all ${
            completedPrompts.has(currentPromptIndex)
              ? 'bg-green-500/20 text-green-400'
              : 'bg-purple-600 hover:bg-purple-700 text-white'
          }`}
        >
          {completedPrompts.has(currentPromptIndex) ? (
            '✓ Recorded'
          ) : (
            'Mark as Recorded'
          )}
        </button>
      </div>

      {/* Tips */}
      <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <p className="text-sm text-blue-300">
          💡 <strong>Tip:</strong> You don't need to record all prompts. 
          Minimum 10 seconds required, but 1-3 minutes gives best results.
        </p>
      </div>
    </div>
  );
}

export default TrainingPrompts;
